import React from 'react';
import { AppShell, Box, Title, Text, Center, Container, Grid, Card, ThemeIcon, Stack, Group, Button, SimpleGrid, Badge, List, Accordion, Divider } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconClock, IconTrendingUp, IconCheck, IconRocket, IconBrain, IconShield, IconChartBar, IconUsers, IconArrowRight, IconFileText, IconDatabase, IconSettings, IconPhone } from '@tabler/icons-react';
import Header from './components/Header';

function App() {
  return (
    <AppShell
      header={{ height: 60 }}
      padding={0}
    >
      <Header />
      
      <AppShell.Main>
        {/* Hero Section */}
        <Box
          style={{
            minHeight: '85vh',
            paddingTop: 80,
            paddingBottom: 60,
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #eff6ff 0%, rgba(255, 255, 255, 1) 50%, #ecfeff 100%)',
          }}
        >
          {/* Background Animation Elements */}
          <Box
            style={{
              position: 'absolute',
              top: -160,
              right: -160,
              width: 320,
              height: 320,
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.2), rgba(6, 182, 212, 0.2))',
              borderRadius: '50%',
              filter: 'blur(40px)',
              animation: 'float 6s ease-in-out infinite',
              opacity: 0.6,
            }}
          />
          <Box
            style={{
              position: 'absolute',
              bottom: -160,
              left: -160,
              width: 320,
              height: 320,
              background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2), rgba(37, 99, 235, 0.2))',
              borderRadius: '50%',
              filter: 'blur(40px)',
              animation: 'float 6s ease-in-out infinite 2s',
              opacity: 0.6,
            }}
          />

          <Container size="xl" style={{ position: 'relative', padding: '0 1.5rem' }}>
            <Grid align="center" gutter="xl">
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Stack gap="lg">
                  <Badge
                    size="md"
                    variant="light"
                    color="blue"
                    leftSection={
                      <Box
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: '#2563eb',
                          animation: 'pulse 2s infinite',
                        }}
                      />
                    }
                  >
                    🚀 AI-Powered Order Automation
                  </Badge>

                  <Title
                    order={1}
                    size="h1"
                    style={{
                      fontSize: 'clamp(2rem, 4vw, 3rem)',
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: '#1a1b1e',
                      marginBottom: '0.5rem',
                    }}
                  >
                    Stop Wasting Hours on{' '}
                    <Text
                      component="span"
                      style={{
                        background: 'linear-gradient(135deg, #2563eb, #06b6d4)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)',
                        fontWeight: 800,
                        display: 'block',
                        marginTop: '0.25rem',
                      }}
                    >
                      Manual Order Entry
                    </Text>
                  </Title>

                  <Text size="lg" c="dimmed" style={{ maxWidth: 500, lineHeight: 1.5, marginBottom: '1rem' }}>
                    Bring speed, accuracy, and automation to your quote-to-cash workflow — with AI that understands your business.
                  </Text>

                  <Group gap="md">
                    <Button
                      size="md"
                      leftSection={<IconPhone size={18} />}
                      style={{
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        border: 'none',
                        padding: '12px 24px',
                        fontSize: '1rem',
                        borderRadius: '10px',
                        boxShadow: '0 6px 20px rgba(37, 99, 235, 0.3)',
                        transition: 'all 0.3s ease',
                      }}
                      component="a"
                      href="#contact"
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 8px 25px rgba(37, 99, 235, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.3)';
                      }}
                    >
                      Book a Free Discovery Call
                    </Button>
                  </Group>
                </Stack>
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, lg: 6 }}>
                <Box
                  style={{
                    position: 'relative',
                    animation: 'float 3s ease-in-out infinite',
                    marginTop: '1rem',
                  }}
                >
                  {/* Enhanced Process Flow Animation */}
                  <Box
                    style={{
                      position: 'relative',
                      padding: '1.5rem',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.6))',
                      borderRadius: '20px',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 15px 35px rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    <Stack gap="lg">
                      <Title order={4} ta="center" c="dark.7" mb="sm" style={{ fontSize: '1.1rem' }}>
                        Automated Process Flow
                      </Title>
                      
                      {/* Step 1: PDF Input */}
                      <Box style={{ position: 'relative' }}>
                        <Card
                          shadow="md"
                          padding="md"
                          radius="lg"
                          withBorder
                          style={{
                            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                            color: 'white',
                            border: 'none',
                            animation: 'slideInLeft 0.8s ease-out',
                          }}
                        >
                          <Group gap="sm" justify="center">
                            <ThemeIcon size={35} radius="lg" color="white" variant="light">
                              <IconFileText size={20} color="#3b82f6" />
                            </ThemeIcon>
                            <Stack gap={0}>
                              <Text fw={600} size="sm">PDF Order Received</Text>
                              <Text size="xs" opacity={0.9}>Any format, any complexity</Text>
                            </Stack>
                          </Group>
                        </Card>
                        
                        {/* Animated Arrow */}
                        <Box
                          style={{
                            position: 'absolute',
                            bottom: -20,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            animation: 'bounce 2s infinite',
                            zIndex: 10,
                          }}
                        >
                          <Box
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            }}
                          >
                            <IconArrowRight size={18} color="#06b6d4" style={{ transform: 'rotate(90deg)' }} />
                          </Box>
                        </Box>
                      </Box>

                      {/* Step 2: AI Processing */}
                      <Box style={{ position: 'relative', marginTop: '1rem' }}>
                        <Card
                          shadow="md"
                          padding="md"
                          radius="lg"
                          withBorder
                          style={{
                            background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                            color: 'white',
                            border: 'none',
                            animation: 'slideInRight 0.8s ease-out 0.3s both',
                          }}
                        >
                          <Group gap="sm" justify="center">
                            <ThemeIcon size={35} radius="lg" color="white" variant="light">
                              <IconBrain size={20} color="#06b6d4" />
                            </ThemeIcon>
                            <Stack gap={0}>
                              <Text fw={600} size="sm">AI Processing</Text>
                              <Text size="xs" opacity={0.9}>Extract, validate, verify</Text>
                            </Stack>
                          </Group>
                        </Card>
                        
                        {/* Animated Arrow */}
                        <Box
                          style={{
                            position: 'absolute',
                            bottom: -20,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            animation: 'bounce 2s infinite 0.5s',
                            zIndex: 10,
                          }}
                        >
                          <Box
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            }}
                          >
                            <IconArrowRight size={18} color="#10b981" style={{ transform: 'rotate(90deg)' }} />
                          </Box>
                        </Box>
                      </Box>

                      {/* Step 3: Clean Sales Order */}
                      <Box style={{ marginTop: '1rem' }}>
                        <Card
                          shadow="md"
                          padding="md"
                          radius="lg"
                          withBorder
                          style={{
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            border: 'none',
                            animation: 'slideInUp 0.8s ease-out 0.6s both',
                          }}
                        >
                          <Group gap="sm" justify="center">
                            <ThemeIcon size={35} radius="lg" color="white" variant="light">
                              <IconDatabase size={20} color="#10b981" />
                            </ThemeIcon>
                            <Stack gap={0}>
                              <Text fw={600} size="sm">Clean Sales Order</Text>
                              <Text size="xs" opacity={0.9}>Ready for ERP integration</Text>
                            </Stack>
                          </Group>
                        </Card>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Grid.Col>
            </Grid>
          </Container>
        </Box>

        {/* Section 1: The Problem */}
        <Box style={{ padding: '80px 0', backgroundColor: '#f8fafc', position: 'relative' }}>
          {/* Background decoration */}
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(245, 101, 101, 0.05) 100%)',
              zIndex: 0,
            }}
          />
          
          <Container size="xl" style={{ position: 'relative', zIndex: 1, padding: '0 1.5rem' }}>
            <Stack gap="xl" align="center" mb="3rem">
              <Badge
                size="lg"
                variant="light"
                color="red"
                style={{
                  fontSize: '0.875rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '2rem',
                }}
              >
                ⚠️ The Problem
              </Badge>
              
              <Title 
                order={2} 
                ta="center" 
                style={{ 
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  fontWeight: 800,
                  color: '#1f2937',
                  lineHeight: 1.2,
                  marginBottom: '1rem',
                }}
              >
                The Hidden Cost of{' '}
                <Text
                  component="span"
                  style={{
                    color: '#dc2626',
                    position: 'relative',
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    lineHeight: 'inherit',
                  }}
                >
                  Manual Orders
                  <Box
                    style={{
                      position: 'absolute',
                      bottom: -4,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: 'linear-gradient(90deg, #dc2626, #ef4444)',
                      borderRadius: '2px',
                      opacity: 0.3,
                    }}
                  />
                </Text>
              </Title>
              
              <Text 
                size="lg" 
                c="gray.6" 
                ta="center" 
                style={{ 
                  maxWidth: 700, 
                  lineHeight: 1.6,
                  fontSize: '1.125rem',
                }}
              >
                Your teams spend hours every week re-entering orders, validating SKUs, and fixing costly mistakes that could be automated away.
              </Text>
            </Stack>

            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
              <Card 
                shadow="lg" 
                padding="xl" 
                radius="xl" 
                withBorder 
                style={{ 
                  transition: 'all 0.3s ease',
                  border: '1px solid #fee2e2',
                  backgroundColor: '#fefefe',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(239, 68, 68, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                {/* Card accent */}
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #ef4444, #f87171)',
                  }}
                />
                
                <Stack gap="lg" align="center">
                  <Box
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #fecaca',
                    }}
                  >
                    <IconRefresh size={36} color="#ef4444" />
                  </Box>
                  
                  <Title order={3} ta="center" style={{ fontSize: '1.25rem', color: '#1f2937', fontWeight: 700 }}>
                    Re-entering Data
                  </Title>
                  
                  <Text c="gray.6" ta="center" size="md" style={{ lineHeight: 1.6 }}>
                    Re-entering the same data in ERP systems, creating inefficiencies and human errors that compound over time
                  </Text>
                  
                  <Badge color="red" variant="light" size="md" style={{ marginTop: '0.5rem' }}>
                    Time Wasted Daily
                  </Badge>
                </Stack>
              </Card>

              <Card 
                shadow="lg" 
                padding="xl" 
                radius="xl" 
                withBorder 
                style={{ 
                  transition: 'all 0.3s ease',
                  border: '1px solid #fed7aa',
                  backgroundColor: '#fefefe',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(251, 146, 60, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                {/* Card accent */}
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #f97316, #fb923c)',
                  }}
                />
                
                <Stack gap="lg" align="center">
                  <Box
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #fff7ed, #fed7aa)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #fdba74',
                    }}
                  >
                    <IconAlertTriangle size={36} color="#f97316" />
                  </Box>
                  
                  <Title order={3} ta="center" style={{ fontSize: '1.25rem', color: '#1f2937', fontWeight: 700 }}>
                    Order Errors
                  </Title>
                  
                  <Text c="gray.6" ta="center" size="md" style={{ lineHeight: 1.6 }}>
                    Order errors lead to fulfillment delays, frustrated customers, and costly returns that damage relationships
                  </Text>
                  
                  <Badge color="orange" variant="light" size="md" style={{ marginTop: '0.5rem' }}>
                    Customer Impact
                  </Badge>
                </Stack>
              </Card>

              <Card 
                shadow="lg" 
                padding="xl" 
                radius="xl" 
                withBorder 
                style={{ 
                  transition: 'all 0.3s ease',
                  border: '1px solid #fef3c7',
                  backgroundColor: '#fefefe',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(245, 158, 11, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                {/* Card accent */}
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                  }}
                />
                
                <Stack gap="lg" align="center">
                  <Box
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #fde68a',
                    }}
                  >
                    <IconClock size={36} color="#f59e0b" />
                  </Box>
                  
                  <Title order={3} ta="center" style={{ fontSize: '1.25rem', color: '#1f2937', fontWeight: 700 }}>
                    Slow Cash Flow
                  </Title>
                  
                  <Text c="gray.6" ta="center" size="md" style={{ lineHeight: 1.6 }}>
                    Late invoices result in slower cash flow and extended payment cycles that impact business growth
                  </Text>
                  
                  <Badge color="yellow" variant="light" size="md" style={{ marginTop: '0.5rem' }}>
                    Revenue Impact
                  </Badge>
                </Stack>
              </Card>
            </SimpleGrid>

            <Box mt="3rem" pt="2rem">
              <Card 
                shadow="xl" 
                padding="2rem" 
                radius="xl" 
                style={{ 
                  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  border: '1px solid #e2e8f0',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Quote decoration */}
                <Box
                  style={{
                    position: 'absolute',
                    top: -20,
                    left: 30,
                    fontSize: '4rem',
                    color: '#cbd5e1',
                    fontFamily: 'Georgia, serif',
                    lineHeight: 1,
                  }}
                >
                  "
                </Box>
                
                <Stack gap="lg" align="center" style={{ position: 'relative', zIndex: 1 }}>
                  <Text 
                    size="xl" 
                    ta="center" 
                    style={{ 
                      fontStyle: 'italic', 
                      lineHeight: 1.6,
                      color: '#374151',
                      fontSize: '1.25rem',
                      fontWeight: 500,
                      maxWidth: 600,
                    }}
                  >
                    We lose 2–3 days just fixing orders and getting them approved. It's the most frustrating part of our workflow.
                  </Text>
                  
                  <Group gap="md" justify="center">
                    <Box
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconUsers size={24} color="white" />
                    </Box>
                    <Stack gap={0}>
                      <Text size="md" fw={600} c="gray.8">
                        VP of Operations
                      </Text>
                      <Text size="sm" c="gray.6">
                        Manufacturing Company
                      </Text>
                    </Stack>
                  </Group>
                </Stack>
              </Card>
            </Box>
          </Container>
        </Box>

        {/* Section 2: Our Solution */}
        <Box style={{ padding: '60px 0', backgroundColor: 'white' }}>
          <Container size="xl" style={{ padding: '0 1.5rem' }}>
            <Stack gap="lg" align="center" mb="xl">
              <Title order={2} ta="center" size="h2" c="green.7" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>
                AI-Powered Order Capture — Built for Manufacturing
              </Title>
              <Text size="md" c="dimmed" ta="center" style={{ maxWidth: 600, lineHeight: 1.6 }}>
                We automate the most painful part of your order-to-cash cycle with AI that understands your business.
              </Text>
            </Stack>

            <Grid align="center" gutter="lg">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack gap="md">
                  <List spacing="sm" size="md">
                    <List.Item icon={<IconCheck size={18} color="#2563eb" />}>
                      <Text fw={500}>Extract from PDFs, Excel, Emails</Text>
                    </List.Item>
                    <List.Item icon={<IconCheck size={18} color="#2563eb" />}>
                      <Text fw={500}>Validate SKUs against ERP/PIM</Text>
                    </List.Item>
                    <List.Item icon={<IconCheck size={18} color="#2563eb" />}>
                      <Text fw={500}>Auto-create Sales Orders or flag for review</Text>
                    </List.Item>
                    <List.Item icon={<IconCheck size={18} color="#2563eb" />}>
                      <Text fw={500}>Works with SAP, Oracle, NetSuite, and more</Text>
                    </List.Item>
                  </List>
                </Stack>
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="lg" padding="lg" radius="lg" withBorder>
                  <Stack gap="md">
                    <Title order={4} ta="center" style={{ fontSize: '1.1rem' }}>Process Flow</Title>
                    <Stack gap="sm">
                      <Group gap="md">
                        <ThemeIcon size={35} radius="md" color="blue">
                          <IconFileText size={20} />
                        </ThemeIcon>
                        <Stack gap={0}>
                          <Text fw={500} size="sm">1. AI Extraction</Text>
                          <Text size="xs" c="dimmed">Read order data from any format</Text>
                        </Stack>
                      </Group>
                      <Group gap="md">
                        <ThemeIcon size={35} radius="md" color="cyan">
                          <IconShield size={20} />
                        </ThemeIcon>
                        <Stack gap={0}>
                          <Text fw={500} size="sm">2. ERP Validation</Text>
                          <Text size="xs" c="dimmed">Validate against master data</Text>
                        </Stack>
                      </Group>
                      <Group gap="md">
                        <ThemeIcon size={35} radius="md" color="green">
                          <IconDatabase size={20} />
                        </ThemeIcon>
                        <Stack gap={0}>
                          <Text fw={500} size="sm">3. Auto-Processing</Text>
                          <Text size="xs" c="dimmed">Create clean sales orders</Text>
                        </Stack>
                      </Group>
                    </Stack>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Container>
        </Box>

        {/* Section 3: Measurable Results */}
        <Box id="results" style={{ padding: '60px 0', backgroundColor: '#1a1b1e', color: 'white' }}>
          <Container size="xl" style={{ padding: '0 1.5rem' }}>
            <Stack gap="lg" align="center" mb="xl">
              <Title order={2} ta="center" size="h2" c="white" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>
                Results You Can Count On
              </Title>
            </Stack>

            <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="lg">
              <Card shadow="lg" padding="lg" radius="lg" style={{ 
                backgroundColor: '#2d3748',
                border: '1px solid #4a5568',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="sm" align="center">
                  <Text size="3xl" fw={700} c="blue.4">
                    80%+
                  </Text>
                  <Text size="md" fw={500} c="white" ta="center">
                    Orders Auto-Processed
                  </Text>
                  <Text size="xs" c="gray.4" ta="center">
                    No human entry needed
                  </Text>
                </Stack>
              </Card>

              <Card shadow="lg" padding="lg" radius="lg" style={{ 
                backgroundColor: '#2d3748',
                border: '1px solid #4a5568',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="sm" align="center">
                  <Text size="3xl" fw={700} c="green.4">
                    2-5 Days
                  </Text>
                  <Text size="md" fw={500} c="white" ta="center">
                    DSO Improvement
                  </Text>
                  <Text size="xs" c="gray.4" ta="center">
                    Faster cash collection
                  </Text>
                </Stack>
              </Card>

              <Card shadow="lg" padding="lg" radius="lg" style={{ 
                backgroundColor: '#2d3748',
                border: '1px solid #4a5568',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="sm" align="center">
                  <Text size="3xl" fw={700} c="cyan.4">
                    50-70%
                  </Text>
                  <Text size="md" fw={500} c="white" ta="center">
                    Time Saved
                  </Text>
                  <Text size="xs" c="gray.4" ta="center">
                    Processing efficiency
                  </Text>
                </Stack>
              </Card>

              <Card shadow="lg" padding="lg" radius="lg" style={{ 
                backgroundColor: '#2d3748',
                border: '1px solid #4a5568',
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="sm" align="center">
                  <Text size="3xl" fw={700} c="orange.4">
                    Zero
                  </Text>
                  <Text size="md" fw={500} c="white" ta="center">
                    ERP Disruption
                  </Text>
                  <Text size="xs" c="gray.4" ta="center">
                    Seamless integration
                  </Text>
                </Stack>
              </Card>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Section 4: Social Proof */}
        <Box style={{ padding: '60px 0', backgroundColor: '#f1f3f4' }}>
          <Container size="xl" style={{ padding: '0 1.5rem' }}>
            <Stack gap="lg" align="center" mb="xl">
              <Title order={2} ta="center" size="h2" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>
                What Our Customers Say
              </Title>
            </Stack>

            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
              <Card shadow="lg" padding="lg" radius="lg" withBorder style={{ 
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="md">
                  <Group gap="md">
                    <ThemeIcon size={40} radius="lg" color="blue" variant="light">
                      <IconUsers size={20} />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text fw={500} size="sm">VP of Operations</Text>
                      <Text size="xs" c="dimmed">Industrial Manufacturer</Text>
                    </Stack>
                  </Group>
                  <Text style={{ fontStyle: 'italic', lineHeight: 1.5 }} size="sm">
                    "We were buried in manual order entry — now our team focuses on growing accounts, not fixing typos."
                  </Text>
                </Stack>
              </Card>

              <Card shadow="lg" padding="lg" radius="lg" withBorder style={{ 
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="md">
                  <Group gap="md">
                    <ThemeIcon size={40} radius="lg" color="green" variant="light">
                      <IconTrendingUp size={20} />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text fw={500} size="sm">CFO</Text>
                      <Text size="xs" c="dimmed">Precision Parts Supplier</Text>
                    </Stack>
                  </Group>
                  <Text style={{ fontStyle: 'italic', lineHeight: 1.5 }} size="sm">
                    "The AI integration paid for itself in under 60 days. Our invoicing cycle is smoother, and our customers noticed."
                  </Text>
                </Stack>
              </Card>

              <Card shadow="lg" padding="lg" radius="lg" withBorder style={{ 
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="md">
                  <Group gap="md">
                    <ThemeIcon size={40} radius="lg" color="cyan" variant="light">
                      <IconSettings size={20} />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text fw={500} size="sm">Director of IT</Text>
                      <Text size="xs" c="dimmed">Electronics Manufacturer</Text>
                    </Stack>
                  </Group>
                  <Text style={{ fontStyle: 'italic', lineHeight: 1.5 }} size="sm">
                    "This isn't another AI dashboard. It's practical automation that just works."
                  </Text>
                </Stack>
              </Card>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Section 5: How It Works */}
        <Box id="how-it-works" style={{ padding: '60px 0', backgroundColor: 'white' }}>
          <Container size="xl" style={{ padding: '0 1.5rem' }}>
            <Stack gap="lg" align="center" mb="xl">
              <Title order={2} ta="center" size="h2" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>
                4-Week Pilot → Real ROI, No Risk
              </Title>
              <Text size="md" c="dimmed" ta="center" style={{ maxWidth: 600, lineHeight: 1.6 }}>
                Our proven engagement model delivers measurable results with zero lock-in.
              </Text>
            </Stack>

            <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="lg">
              <Card shadow="lg" padding="lg" radius="lg" withBorder style={{ 
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="md" align="center">
                  <ThemeIcon size={50} radius="md" color="blue">
                    <IconPhone size={26} />
                  </ThemeIcon>
                  <Text fw={500} ta="center" size="sm">1. Discovery Call</Text>
                  <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1.5 }}>
                    Analyze your current order workflow and pain points
                  </Text>
                </Stack>
              </Card>

              <Card shadow="lg" padding="lg" radius="lg" withBorder style={{ 
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="md" align="center">
                  <ThemeIcon size={50} radius="md" color="cyan">
                    <IconChartBar size={26} />
                  </ThemeIcon>
                  <Text fw={500} ta="center" size="sm">2. Analyze Orders</Text>
                  <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1.5 }}>
                    Review your order formats and ERP integration points
                  </Text>
                </Stack>
              </Card>

              <Card shadow="lg" padding="lg" radius="lg" withBorder style={{ 
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="md" align="center">
                  <ThemeIcon size={50} radius="md" color="green">
                    <IconRocket size={26} />
                  </ThemeIcon>
                  <Text fw={500} ta="center" size="sm">3. Deploy AI Agent</Text>
                  <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1.5 }}>
                    4-week pilot with subset of live orders
                  </Text>
                </Stack>
              </Card>

              <Card shadow="lg" padding="lg" radius="lg" withBorder style={{ 
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)' }
              }}>
                <Stack gap="md" align="center">
                  <ThemeIcon size={50} radius="md" color="orange">
                    <IconTrendingUp size={26} />
                  </ThemeIcon>
                  <Text fw={500} ta="center" size="sm">4. Measure ROI</Text>
                  <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1.5 }}>
                    Track % auto-processed, time saved, DSO improvement
                  </Text>
                </Stack>
              </Card>
            </SimpleGrid>

            <Center mt="lg">
              <Button 
                size="md" 
                color="green" 
                leftSection={<IconArrowRight size={18} />}
                style={{
                  padding: '12px 24px',
                  fontSize: '1rem',
                  borderRadius: '10px',
                  boxShadow: '0 6px 20px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.3)';
                }}
              >
                Get Started with a Paid Pilot
              </Button>
            </Center>
          </Container>
        </Box>

        {/* Section 6: FAQ */}
        <Box id="faq" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
          <Container size="xl" style={{ padding: '0 1.5rem' }}>
            <Stack gap="lg" align="center" mb="xl">
              <Title order={2} ta="center" size="h2" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>
                Frequently Asked Questions
              </Title>
            </Stack>

            <Box style={{ maxWidth: 700, margin: '0 auto' }}>
              <Accordion variant="contained" radius="lg">
                <Accordion.Item value="erp">
                  <Accordion.Control>
                    <Text fw={500} size="sm">Do we need to change our ERP or CRM?</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text style={{ lineHeight: 1.5 }} size="sm">
                      No. We integrate with what you already use — SAP, Oracle, Dynamics, NetSuite, and more. No rip-and-replace required.
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="accuracy">
                  <Accordion.Control>
                    <Text fw={500} size="sm">How accurate is the order extraction?</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text style={{ lineHeight: 1.5 }} size="sm">
                      90–95%+ accuracy on structured PDFs or Excel sheets, with human review only needed on edge cases.
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="security">
                  <Accordion.Control>
                    <Text fw={500} size="sm">What about security and data privacy?</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text style={{ lineHeight: 1.5 }} size="sm">
                      Your data is encrypted in transit and at rest. We're happy to sign NDAs and deploy on-prem or in your private cloud if needed.
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="formats">
                  <Accordion.Control>
                    <Text fw={500} size="sm">What if my order formats are all different?</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text style={{ lineHeight: 1.5 }} size="sm">
                      That's exactly what we solve — our AI learns from your variations and normalizes inputs from email, PDF, Excel, or scanned docs.
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="timeline">
                  <Accordion.Control>
                    <Text fw={500} size="sm">How long to go live?</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text style={{ lineHeight: 1.5 }} size="sm">
                      Typical pilot setup is 2–4 weeks. You'll see ROI in under 60 days.
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Box>
          </Container>
        </Box>

        {/* Final CTA Banner */}
        <Box id="contact" style={{ padding: '60px 0', backgroundColor: '#1a1b1e', color: 'white' }}>
          <Container size="xl" style={{ padding: '0 1.5rem' }}>
            <Stack gap="lg" align="center">
              <Title order={2} ta="center" size="h2" c="white" style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>
                Ready to Eliminate Manual Order Entry?
              </Title>
              <Text size="md" c="gray.4" ta="center" style={{ maxWidth: 500, lineHeight: 1.6 }}>
                Let's automate the pain, not the people. Schedule your free discovery call today.
              </Text>
              
              <Button
                size="lg"
                leftSection={<IconPhone size={20} />}
                style={{
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  border: 'none',
                  fontSize: '1.1rem',
                  padding: '16px 32px',
                  borderRadius: '12px',
                  boxShadow: '0 8px 30px rgba(37, 99, 235, 0.4)',
                  transition: 'all 0.3s ease',
                  marginTop: '0.5rem',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 12px 40px rgba(37, 99, 235, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 30px rgba(37, 99, 235, 0.4)';
                }}
              >
                Book a Discovery Call
              </Button>

              <Divider color="gray.7" style={{ width: '100%', margin: '2rem 0' }} />

              <Grid align="center" gutter="lg" style={{ width: '100%' }}>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Stack gap="xs" align="center">
                    <Text fw={500} c="white" size="sm">Email</Text>
                    <Text c="gray.4" size="sm">hello@kitchain.ai</Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Stack gap="xs" align="center">
                    <Text fw={500} c="white" size="sm">Phone</Text>
                    <Text c="gray.4" size="sm">+1 (555) 123-4567</Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Stack gap="xs" align="center">
                    <Text fw={500} c="white" size="sm">Response Time</Text>
                    <Badge color="green" variant="light" size="md">
                      Within 24 hours
                    </Badge>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Stack>
          </Container>
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}

export default App;
